import { useCallback, useEffect, useMemo } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'next-i18next';
import type { Cart, RemoveItemInput } from '../../codegen/types';
import { useCartContext } from '../../context/cartContext/useCartContext';
import { useLocaleContext } from '../../context/localeContext';
import { useStaticContext } from '../../context/staticContext';
import { useRemoveItemMutation } from '../../graphqlDocument/cart/removeItem.generated';
import { useFreeGiftModal } from '../../hooks/useFreeGiftModal';
import { useNRBASession } from '../../hooks/useNRBASession';
import { sendRemoveFromCartFromProductLineItemEvent } from '../../utils/gtm/events/remove_from_cart/sendRemoveFromCartFromProductLineItemEvent';
import { newRelicAvailabilityChecker } from '../../utils/newrelic';
import { InformationPopUp } from '../popUps';
import { removeFreeGiftPopupCookie } from './freeGiftUtils';

export function FreeGiftOutOfStockModal(): ReactElement {
  const { t } = useTranslation(['lib-global-common']);
  const { cart, cartId, anonymousId, setCart, setShouldUpdateDeliveryOptions, refetchCartWithCurrentIds } =
    useCartContext();
  const discountItems = cart?.discountItems;
  const { locale } = useLocaleContext();
  const { baseUrl } = useStaticContext();
  const nrbaContextHeader = useNRBASession();
  const [removeItemMutation] = useRemoveItemMutation(nrbaContextHeader);
  const { isVisible, showModal, hideModal } = useFreeGiftModal();

  // Memoize out of stock gift logic
  const outOfStockGift = useMemo(() => {
    // Early exit if no discount items
    if (!discountItems?.length) return null;

    return discountItems.find((item) => {
      if (!item?.lineItemId || !item.isFreeGiftOutOfStock) return false;
      return item.isFreeGiftOutOfStock;
    });
  }, [discountItems]);

  useEffect(() => {
    if (outOfStockGift?.lineItemId) {
      showModal();
    }
  }, [outOfStockGift, showModal]);

  const removeCookieForCurrentGift = useCallback(() => {
    if (outOfStockGift?.lineItemId) {
      removeFreeGiftPopupCookie(outOfStockGift.lineItemId, locale);
    }
  }, [outOfStockGift, locale]);

  const handleRemoveOutOfStockGift = useCallback(async () => {
    if (outOfStockGift?.lineItemId) {
      const { lineItemId } = outOfStockGift;
      try {
        const input: RemoveItemInput = {
          cartId,
          anonymousId,
          locale,
          lineItemId,
        };

        const { data } = await removeItemMutation({
          variables: { input },
        });

        const removedItem = data?.removeItem as Cart;
        setCart(removedItem);
        setShouldUpdateDeliveryOptions(true);
        sendRemoveFromCartFromProductLineItemEvent(cart, lineItemId, removedItem, baseUrl);
        refetchCartWithCurrentIds();
      } catch (error) {
        console.error('Error removing out of stock gift:', error);
        newRelicAvailabilityChecker(() => {
          window.newrelic.noticeError(new Error(`Error removing out of stock gift: ${error}`));
        });
      }
    }
  }, [
    outOfStockGift,
    cartId,
    anonymousId,
    removeItemMutation,
    setCart,
    setShouldUpdateDeliveryOptions,
    baseUrl,
    locale,
    refetchCartWithCurrentIds,
    cart,
  ]);

  const handleClose = useCallback(() => {
    handleRemoveOutOfStockGift();
    removeCookieForCurrentGift();
    hideModal();
  }, [removeCookieForCurrentGift, handleRemoveOutOfStockGift, hideModal]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        handleClose();
      }
    },
    [handleClose],
  );

  return (
    <InformationPopUp
      isOpen={isVisible}
      message={t('free.gift.out.of.stock.message')}
      description={t('free.gift.out.of.stock.description')}
      confirmationLabel={t('free.gift.ok.button.label')}
      confirmationText={t('free.gift.ok.button')}
      handleCloseOverlay={handleClose}
      handleOpenChange={handleOpenChange}
      data-testid="freeGiftOutOfStockModal"
    />
  );
}
